import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { TrendingUp, TrendingDown, Users as UsersIcon, Building2, GraduationCap, FileBadge, AlertTriangle, DollarSign, Activity } from "lucide-react";

const KPI_GRID = [
  { key: "users", label: "Total Users", icon: UsersIcon },
  { key: "professionals", label: "Professionals", icon: UsersIcon },
  { key: "students", label: "Students", icon: GraduationCap },
  { key: "freelancers", label: "Freelancers", icon: UsersIcon },
  { key: "organizations", label: "Organizations", icon: Building2 },
  { key: "institutions", label: "Institutions", icon: GraduationCap },
  { key: "certificates", label: "Certificates", icon: FileBadge },
  { key: "conduct_certificates", label: "Conduct Certs", icon: FileBadge },
  { key: "recommendations", label: "Recommendations", icon: FileBadge },
  { key: "verifications", label: "Verifications", icon: Activity },
  { key: "disputes", label: "Disputes", icon: AlertTriangle },
  { key: "active_subscriptions", label: "Active Subs", icon: DollarSign },
];

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <AdminLayout title="Loading…" subtitle="Master Control" />;

  const t = data.totals;
  const g = data.growth;
  const maxUsers = Math.max(1, ...g.series_30d.map((s) => s.users));
  const maxCerts = Math.max(1, ...g.series_30d.map((s) => s.certs));

  return (
    <AdminLayout subtitle="Master Control" title="Global Dashboard" actions={<RevenueBadge revenue={t.revenue} />}>
      {/* KPI grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-white/10" data-testid="kpi-grid">
        {KPI_GRID.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.key} className="bg-ec-ink p-5 hover:bg-white/5 transition-colors" data-testid={`kpi-${k.key}`}>
              <div className="flex items-center justify-between">
                <div className="eyebrow text-zinc-500">{k.label}</div>
                <Icon className="w-3.5 h-3.5 text-ec-emerald" />
              </div>
              <div className="font-display text-3xl font-black mt-2">{(t[k.key] || 0).toLocaleString()}</div>
            </div>
          );
        })}
      </div>

      {/* Growth row */}
      <div className="grid sm:grid-cols-3 gap-6 mt-10">
        <GrowthCard label="Daily Signups" value={g.daily_signups} period="last 24h" />
        <GrowthCard label="Weekly Signups" value={g.weekly_signups} period="last 7 days" />
        <GrowthCard label="Monthly Signups" value={g.monthly_signups} period="last 30 days" />
      </div>

      {/* 30-day series chart */}
      <div className="mt-10 bg-white/[0.03] border border-white/10 rounded-sm p-6" data-testid="series-chart">
        <div className="flex items-center justify-between">
          <div>
            <div className="eyebrow text-ec-emerald">Last 30 days</div>
            <h3 className="font-display text-xl font-bold mt-1">Growth & certificate issuance</h3>
          </div>
          <div className="flex items-center gap-4 text-xs font-mono-plex">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-ec-emerald rounded-full" /> Users</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-amber-400 rounded-full" /> Certs</span>
          </div>
        </div>
        <div className="mt-6 h-44 flex items-end gap-1" data-testid="series-chart-bars">
          {g.series_30d.map((s, i) => (
            <div key={i} className="flex-1 h-full flex flex-col justify-end group relative">
              <div
                title={`${s.date}: ${s.users} users, ${s.certs} certs`}
                className="bg-amber-400/80 hover:bg-amber-400 transition-colors w-full"
                style={{ height: `${Math.max(2, (s.certs / maxCerts) * 30)}%` }}
              />
              <div
                className="bg-ec-emerald hover:bg-ec-emerald/80 transition-colors w-full"
                style={{ height: `${Math.max(4, (s.users / maxUsers) * 65)}%` }}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-between text-[10px] font-mono-plex text-zinc-500 mt-2">
          <span>{g.series_30d[0]?.date}</span>
          <span>{g.series_30d[g.series_30d.length - 1]?.date}</span>
        </div>
      </div>
    </AdminLayout>
  );
}

function GrowthCard({ label, value, period }) {
  return (
    <div className="bg-white/[0.03] border border-white/10 p-6 rounded-sm" data-testid={`growth-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="eyebrow text-zinc-500">{label}</div>
      <div className="flex items-end gap-2 mt-2">
        <div className="font-display text-4xl font-black">{value.toLocaleString()}</div>
        <div className="text-xs text-ec-emerald flex items-center gap-1 pb-1.5">
          <TrendingUp className="w-3 h-3" /> {period}
        </div>
      </div>
    </div>
  );
}
function RevenueBadge({ revenue }) {
  return (
    <div className="bg-ec-emerald/10 border border-ec-emerald/30 px-4 py-2.5 rounded-sm" data-testid="revenue-badge">
      <div className="eyebrow text-ec-emerald">Total Revenue</div>
      <div className="font-display text-2xl font-black">${(revenue || 0).toLocaleString()}</div>
    </div>
  );
}
